const EXCEL_EXTENSIONS = [".xls", ".xlsx", ".xlsm", ".xlsb"];

// Client-side pre-flight so the common, user-fixable mistakes (an Excel file, an
// empty file, a non-CSV) get an actionable message before we ever hit storage,
// instead of surfacing as an opaque HTTP 400 from S3.
export function preflightUploadFile(file: File): string | null {
  const name = file.name;
  const lower = name.toLowerCase();

  if (file.size === 0) {
    return `"${name}" is empty — there's no data to upload.`;
  }
  if (EXCEL_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return `"${name}" looks like an Excel file. In Excel, choose File → Save As → CSV, then upload the .csv.`;
  }
  if (!lower.endsWith(".csv")) {
    return `"${name}" isn't a CSV. Only .csv files are supported — please export your data as CSV and upload that.`;
  }
  return null;
}

interface StorageError {
  code: string | null;
  message: string | null;
}

// The storage PUT (S3 presigned URL, or the local-upload endpoint in dev) answers
// with a real reason on failure that the bare status code hides: JSON
// {error, message} from an AppException, or an <Error><Code><Message> XML body
// from S3. Both carry a machine code alongside the human message.
function parseStorageErrorBody(body: string | null): StorageError {
  if (!body) return { code: null, message: null };
  const trimmed = body.trim();

  if (trimmed.startsWith("{")) {
    try {
      const data = JSON.parse(trimmed);
      const message = data?.message || data?.detail;
      const code = data?.error || data?.code;
      return {
        code: typeof code === "string" && code.trim() ? code.trim() : null,
        message: typeof message === "string" && message.trim() ? message.trim() : null,
      };
    } catch {
      return { code: null, message: null };
    }
  }

  const messageMatch = trimmed.match(/<Message>([^<]+)<\/Message>/i);
  const codeMatch = trimmed.match(/<Code>([^<]+)<\/Code>/i);
  return {
    code: codeMatch ? codeMatch[1].trim() : null,
    message: messageMatch ? messageMatch[1].trim() : null,
  };
}

function errorTag(code: string | null | undefined, status: number | null | undefined): string {
  const bits: string[] = [];
  if (code) bits.push(code);
  if (status) bits.push(`HTTP ${status}`);
  return bits.join(", ");
}

export function describeStorageFailure(xhr: XMLHttpRequest): string {
  const { code, message } = parseStorageErrorBody(xhr.responseText);
  const tag = errorTag(code, xhr.status) || `HTTP ${xhr.status}`;
  if (message) return `Couldn't store your file: ${message} (${tag})`;
  return `Couldn't store your file (${tag}). Please try again, or contact your Motimatic team if it keeps happening.`;
}

export function describePresignFailure(err: unknown): string {
  const message = err instanceof Error && err.message ? err.message : "please try again.";
  const e = err as { code?: string; status?: number } | null;
  const tag = errorTag(e?.code, e?.status);
  return tag ? `Couldn't prepare the upload: ${message} (${tag})` : `Couldn't prepare the upload: ${message}`;
}
